// Ported from cartadigital-pos app/domain/pricing/display/money-display.ts.
import type { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { roundPrice } from '@/utils';
import { applyTax } from '../tax';

export interface IComputeCatalogDisplayAmountInput {
  amount: number;
  taxPercent: number | null | undefined;
  taxMode: TaxDisplayMode;
}

/**
 * Catalog display values are raw menu prices (item.price, optionItem.cost).
 * For display parity with backend/admin:
 * - Apply tax with partial=true (only affects SilentAdd/AddAndShow)
 * - Round to 2 decimals to avoid FP artifacts
 */
export function computeCatalogDisplayAmount(
  input: IComputeCatalogDisplayAmountInput
): number {
  const val =
    typeof input.amount === 'number' && Number.isFinite(input.amount)
      ? input.amount
      : 0;

  const taxed = applyTax({
    taxPercent: input.taxPercent,
    taxMode: input.taxMode,
    price: val,
    partial: true,
  });

  return roundPrice(taxed);
}

export type IVariantLabelSign = '+' | '-' | '';

export interface IComputeVariantLabelPartsInput {
  optionMain: boolean;
  variantsFullPrice: boolean;
  optionPrice: number;
  baseItemPrice: number;
  taxPercent: number | null | undefined;
  taxMode: TaxDisplayMode;
}

export interface IVariantLabelParts {
  show: boolean;
  sign: IVariantLabelSign;
  amount: number;
}

/**
 * Computes variant/add-on label semantics, mirroring admin UX:
 * - Non-main options: show +price when price > 0
 * - Main options:
 *   - variantsFullPrice=true => show full price
 *   - variantsFullPrice=false => show delta vs base item price (+/-), hide when delta=0
 *
 * Tax is applied to the displayed amount using partial=true (catalog display).
 */
export function computeVariantLabelParts(
  input: IComputeVariantLabelPartsInput
): IVariantLabelParts {
  const optionPrice =
    typeof input.optionPrice === 'number' && Number.isFinite(input.optionPrice)
      ? input.optionPrice
      : 0;
  const baseItemPrice =
    typeof input.baseItemPrice === 'number' &&
    Number.isFinite(input.baseItemPrice)
      ? input.baseItemPrice
      : 0;

  if (!input.optionMain) {
    if (optionPrice <= 0) {
      return { show: false, sign: '+', amount: 0 };
    }

    return {
      show: true,
      sign: '+',
      amount: computeCatalogDisplayAmount({
        amount: optionPrice,
        taxPercent: input.taxPercent,
        taxMode: input.taxMode,
      }),
    };
  }

  // Variants (main=true)
  if (input.variantsFullPrice) {
    return {
      show: true,
      sign: '',
      amount: computeCatalogDisplayAmount({
        amount: optionPrice,
        taxPercent: input.taxPercent,
        taxMode: input.taxMode,
      }),
    };
  }

  const diff = roundPrice(optionPrice - baseItemPrice);
  if (diff === 0) {
    return { show: false, sign: '+', amount: 0 };
  }

  return {
    show: true,
    sign: diff > 0 ? '+' : '-',
    amount: computeCatalogDisplayAmount({
      amount: Math.abs(diff),
      taxPercent: input.taxPercent,
      taxMode: input.taxMode,
    }),
  };
}
